import { Struct } from "@bufbuild/protobuf";
import {
	type EditorState as CodeMirrorEditorState,
	Transaction as CodeMirrorTransaction,
} from "@codemirror/state";
import { InvalidArgument } from "@reboot-dev/reboot-api/errors_pb";
import {
	Commit,
	type CommitJSON,
	collabKey,
	receiveCommitTransaction,
	sendableCommit,
} from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import isString from "lodash-es/isString";
import {
	type EditorState as ProseMirrorEditorState,
	Transaction as ProseMirrorTransaction,
} from "prosemirror-state";
import {
	type ActorRefFromLogic,
	type EventObject,
	and,
	assign,
	enqueueActions,
	fromCallback,
	fromPromise,
	not,
	sendParent,
	setup,
} from "xstate";

import { generateShortUuid, pageAuthorityId } from "@moment/api-collab";
import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { isDesktopAppMode } from "~/auth/interop/common";
import { createEditorState } from "~/components/canvas/easel/editor-state/createEditorState";
import { createMarkdownEditorState } from "~/components/canvas/easel/editor-state/createMarkdownEditorState";
import {
	type ChangesResponse,
	PageAuthority,
	PageAuthorityApplyAborted,
	RebootContext,
	getBaseRebootUrl,
} from "~/data/reboot";
import {
	type Cell,
	type CellInfo,
	type CellMap,
	equals,
	proseMirrorEditorStateToCells,
} from "~/models/cell";
import { emitCell } from "~/runtime/cell-compiler/emit";
import { getDocsApiUrl } from "~/state/api/docsapi";
import { type StoredPage } from "~/state/types/editors.types";

import { filesystemApi } from "../../api/filesystem";
import {
	type AuthenticationMachine,
	authenticationActorRef,
	getAccessToken,
} from "../authentication";

const clientID = generateShortUuid();

const logger = new Logger("page.machine");

const loadPageContentsFromDisk = fromPromise<
	{ parsed: StoredPage; raw: string },
	{ documentPath: string; pageId: string }
>(async ({ input }) => {
	return filesystemApi.page.contents.read(input.documentPath, input.pageId);
});

const loadPageContentsFromRemote = fromPromise<
	StoredPage,
	{
		workspaceName?: string;
		documentId: string;
		pageId: string;
	}
>(async ({ input }) => {
	const { workspaceName, documentId, pageId } = input;

	const accessToken = await getAccessToken();

	const headers = new Headers();
	headers.set("Accept", "application/json");

	if (accessToken) {
		headers.set("Authorization", `Bearer ${accessToken}`);
	}

	const response = await fetch(
		`${getDocsApiUrl()}/api/v1/repos/${workspaceName}/${documentId}/raw/pages/${pageId}/index.md?ref=main`,
		{ headers }
	);
	if (!response.ok) {
		throw new Error("Failed to fetch public page metadata");
	}

	const text = await response.text();
	const { doc: jsonDoc, metadata } = await parsePage(text);

	return {
		doc: jsonDoc,
		metadata: {
			id: pageId,
			slug: pageId,
			title: "",
			archived: false,
			parentPageID: null,
			...metadata,
		},
	};
});

const writeEditorStateToDisk = fromPromise(
	async ({
		input,
	}: {
		input: {
			pageId: string;
			documentPath: string;
			editorState: ProseMirrorEditorState | CodeMirrorEditorState;
		};
	}) => {
		if (!isDesktopAppMode()) {
			return Promise.reject();
		}

		return filesystemApi.page.contents.write(
			input.documentPath,
			input.pageId,
			input.editorState.doc
		);
	}
);

const writeMarkdownEditorStateToDisk = fromPromise(
	async ({
		input,
	}: {
		input: { pageId: string; documentPath: string; editorState: CodeMirrorEditorState };
	}) => {
		return filesystemApi.page.contents.write(
			input.documentPath,
			input.pageId,
			input.editorState.doc
		);
	}
);

async function compileCell(cell: Cell, env: MomentAppEnv): Promise<CellInfo> {
	try {
		const emitInfo = await emitCell(cell, {}, env);

		if (emitInfo.kind === "StaticError") {
			return {
				cell,
				status: "error",
				message: emitInfo.message,
			};
		}

		return {
			cell,
			emitInfo,
			status: "executable",
		};
	} catch (error) {
		return {
			cell,
			status: "error",
			message: error instanceof Error ? error.message : "Unknown error",
		};
	}
}

const compileCells = fromPromise(
	async ({
		input,
	}: {
		input: {
			pageId: string;
			previousCells: CellMap;
			editorState: ProseMirrorEditorState;
		};
	}): Promise<CellMap> => {
		const { previousCells, editorState } = input;
		const env = process.env["APP_ENV"];

		const nextCells = proseMirrorEditorStateToCells(
			editorState,
			(node) => node.type === schema.nodes.compute_cell
		);
		const nextCellIDs = new Set(nextCells.map((cell) => cell.id).filter(isString));

		// Compile cells that have changed
		const cellsToCompile = nextCells.filter(
			(cell) => !!cell.id && !equals(cell, previousCells[cell.id]?.cell || {})
		);

		const anyCellsDeleted =
			nextCellIDs.difference(new Set(Object.keys(previousCells))).size > 0;

		if (!anyCellsDeleted && cellsToCompile.length === 0) {
			return previousCells;
		}

		const newCompiledCells = Object.fromEntries(
			await Promise.all(
				cellsToCompile.map(async (cell) => {
					return [cell.id, await compileCell(cell, env)] as const;
				})
			)
		);

		// Merge delete operations with new compiled cells
		const compiledCells: CellMap = {};

		for (const id of nextCellIDs) {
			const newlyCompiledCell = newCompiledCells[id];
			if (newlyCompiledCell) {
				compiledCells[id] = newlyCompiledCell;
				continue;
			}

			const previousCell = previousCells[id];
			if (previousCell) {
				compiledCells[id] = previousCell;
				continue;
			}
		}

		return compiledCells;
	}
);

type RebootInstance = {
	context: RebootContext;
	authority: PageAuthority.WeakReference;
};

const connectToReboot = fromPromise(
	async ({
		input,
	}: {
		input: {
			workspaceName: string;
			documentId: string;
			pageId: string;
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		};
	}): Promise<RebootInstance> => {
		const getBearerToken = async () => {
			const authCtx = authenticationActorRef.getSnapshot().context;
			return authCtx.authnStatus === "authenticated"
				? authCtx.getAccessTokenSilently()
				: undefined;
		};
		const context = new RebootContext({ url: getBaseRebootUrl(), bearerToken: getBearerToken });
		return {
			context,
			authority: PageAuthority.ref(
				pageAuthorityId(input.workspaceName, input.documentId, input.pageId)
			),
		};
	}
);

function serializeForStruct(json: Json) {
	if (typeof json !== "object" || json === null) return json;
	if (Array.isArray(json)) return json.map((j) => serializeForStruct(j));
	return Object.fromEntries(
		Object.entries(json)
			.filter(([key, value]) => value !== undefined)
			.map(([key, value]) => [key, serializeForStruct(value)])
	);
}

const listenForChanges = fromCallback<
	EventObject,
	{
		reboot: RebootInstance;
		editorState: ProseMirrorEditorState;
		workspaceName: string;
		documentId: string;
		pageId: string;
	}
>(({ input, sendBack }) => {
	const { reboot, editorState, workspaceName, documentId, pageId } = input;
	let abortController = new AbortController();

	async function run() {
		const initialDoc = Struct.fromJson(serializeForStruct(editorState.doc.toJSON()));
		await reboot.authority.initialize(reboot.context, {
			doc: initialDoc,
		});

		const { doc, version } = await reboot.authority.page(reboot.context);

		sendBack({
			type: "page.editorStateReconfigured",
			payload: {
				editorState: createEditorState({
					doc: doc.toJson() as JsonObject,
					workspaceName,
					documentId,
					pageId,
					version,
				}),
			},
		});

		let sinceVersion = version;

		// Note: This does need to be awaited. It returns a promise, but is typed incorrectly
		const [stream, update] = await reboot.authority
			.reactively()
			.changes(input.reboot.context, { sinceVersion }, { signal: abortController.signal });

		while (!abortController.signal.aborted) {
			const result = await stream.next();
			if (result.done) break;
			const { commits } = result.value as ChangesResponse;
			const lastCommit = commits.at(-1);

			if (lastCommit) {
				sinceVersion = lastCommit.version;
			}

			sendBack({
				type: "page.remoteTransactionReceived",
				payload: {
					commits: commits.map((commit) => ({
						...commit,
						steps: commit.steps.map((step) => step.toJson()),
					})),
					transactionId: generateShortUuid(),
				},
			});

			if (commits.length) {
				update({ sinceVersion });
			}
		}
	}

	run();

	return () => {
		abortController.abort();
	};
});

const sendToReboot = fromPromise(
	async ({
		input,
	}: {
		input: { reboot: RebootInstance; editorState: ProseMirrorEditorState };
	}) => {
		const sendable = sendableCommit(input.editorState);
		if (!sendable) return null;
		try {
			const { commit } = await input.reboot.authority.apply(input.reboot.context, {
				commit: {
					...sendable,
					steps: sendable.steps.map((step) =>
						Struct.fromJson(serializeForStruct(step.toJSON()))
					),
				},
			});
			return Commit.FromJSON(schema, {
				...commit,
				steps: commit.steps.map((step) => step.toJson() as { [key: string]: unknown }),
			});
		} catch (e) {
			if (e instanceof PageAuthorityApplyAborted && e.cause instanceof InvalidArgument) {
				return null;
			}
			throw e;
		}
	}
);

const writeConfirmedEditorState = fromPromise(
	async ({
		input,
	}: {
		input: { editorState: ProseMirrorEditorState; documentPath: string; pageId: string };
	}) => {
		if (!isDesktopAppMode()) {
			throw new Error("Can't write to disk in web mode");
		}

		const collabState = collabKey.getState(input.editorState);

		const unconfirmed = collabState.unconfirmed;

		// Apply the inversion of each step in reverse order to get back
		// to the confirmed state
		const confirmedState = input.editorState.apply(
			unconfirmed
				.toReversed()
				.reduce((acc, rebaseable) => acc.step(rebaseable.inverted), input.editorState.tr)
		);

		return filesystemApi.page.contents.write(
			input.documentPath,
			input.pageId,
			confirmedState.doc
		);
	}
);

export const pageMachine = setup({
	types: {
		context: {} as {
			workspaceName?: string;
			documentId: string;
			pageId: string;
			pageData?: StoredPage;
			documentPath?: string;
			schemaVersion: string;
			editorMode: "rich" | "markdown";
			theme: "light" | "dark";
			editorState?: ProseMirrorEditorState;
			markdownEditorState?: CodeMirrorEditorState;
			compiledCells: CellMap;
			compilationError: Error | null;
			lastTransactionAppliedId: string | null;
			lastTransactionPersistedId: string | null;
			fetchError: Error | null;
			reboot: RebootInstance | null;
			hasPendingTransaction: boolean;
			collabEnabled: boolean;
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		},
		input: {} as {
			workspaceName?: string;
			documentId: string;
			pageId: string;
			documentPath?: string;
			authenticationActorRef?: ActorRefFromLogic<AuthenticationMachine>;
		},
		events: {} as
			| { type: "document.cloned"; payload: { documentPath: string } }
			| {
					type: "page.opened";
					payload?: { mode: "rich" | "markdown"; theme: "light" | "dark" };
			  }
			| { type: "page.compute" }
			| { type: "page.closed" }
			| { type: "page.archived" }
			| { type: "page.unarchived" }
			| { type: "page.nameChanged"; payload: { newName: string } }
			| { type: "page.slugChanged"; payload: { newSlug: string } }
			| {
					type: "page.transactionDispatched";
					payload: {
						transaction: ProseMirrorTransaction | CodeMirrorTransaction;
						timestamp: Date;
						transactionId: string;
					};
			  }
			| { type: "page.refresh" }
			| { type: "document.collab.enabled" }
			| { type: "document.collab.disabled" }
			| { type: "page.sentToReboot" }
			| {
					type: "page.editorStateReconfigured";
					payload: { editorState: ProseMirrorEditorState };
			  }
			| {
					type: "page.remoteTransactionReceived";
					payload: {
						commits: CommitJSON[];
						transactionId: string;
					};
			  },
	},
	actors: {
		loadPageContentsFromDisk,
		loadPageContentsFromRemote,
		writeEditorStateToDisk,
		writeMarkdownEditorStateToDisk,
		compileCells,
		connectToReboot,
		listenForChanges,
		sendToReboot,
		writeConfirmedEditorState,
	},
	guards: {
		isDesktopAppMode: () => {
			return isDesktopAppMode();
		},

		pageExistsOnDisk: ({ context }) => {
			return Boolean(context.documentPath);
		},

		lastTransactionWasNotPersisted: ({ context }) => {
			return context.lastTransactionPersistedId !== context.lastTransactionAppliedId;
		},

		editorModeIsRich: ({ context }) => {
			return context.editorMode === "rich";
		},

		editorModeIsMarkdown: ({ context }) => {
			return context.editorMode === "markdown";
		},

		isCollabEnabled: ({ context }) => {
			return context.collabEnabled;
		},

		hasPendingTransaction: ({ context }) => {
			return context.hasPendingTransaction;
		},
	},
	actions: {
		applyTransaction: assign(({ context, event }) => {
			if (event.type !== "page.transactionDispatched") {
				return {};
			}

			const { transaction, transactionId } = event.payload;

			if (transaction instanceof ProseMirrorTransaction) {
				if (!context.editorState) {
					logger.error("applyTransaction: No editor state found", {
						context,
						transaction,
					});
					return {};
				}

				const updated = context.editorState.apply(transaction);
				return {
					editorState: updated,
					lastTransactionAppliedId: transactionId,
				};
			}

			if (transaction instanceof CodeMirrorTransaction) {
				if (!context.markdownEditorState) {
					logger.error("applyTransaction: No markdown editor state found", {
						context,
						transaction,
					});
					return {};
				}

				return {
					markdownEditorState: transaction.state,
					lastTransactionAppliedId: transactionId,
				};
			}

			return {};
		}),

		receiveRemoteTransaction: assign(({ context, event }) => {
			if (event.type !== "page.remoteTransactionReceived") {
				return {};
			}

			const { commits, transactionId } = event.payload;

			const newEditorState = commits.reduce(
				(acc, commit) =>
					acc.apply(receiveCommitTransaction(acc, Commit.FromJSON(acc.schema, commit))),
				context.editorState
			);

			return {
				editorState: newEditorState,
				lastTransactionAppliedId: transactionId,
			};
		}),

		clearEditorState: assign(() => ({
			editorState: undefined,
			markdownEditorState: undefined,
			compiledCells: undefined,
			lastTransactionAppliedId: null,
			lastTransactionPersistedId: null,
		})),
	},
}).createMachine({
	id: "page",
	type: "parallel",
	context: ({ input }) => ({
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		pageId: input.pageId,
		editorMode: "rich",
		theme: "light",
		compiledCells: {},
		compilationError: null,
		schemaVersion: "v1alpha1",
		documentPath: input.documentPath,
		lastTransactionAppliedId: null,
		lastTransactionPersistedId: null,
		fetchError: null,
		collabEnabled: false,
		hasPendingTransaction: false,
		reboot: null,
		authenticationActorRef: input.authenticationActorRef ?? authenticationActorRef,
	}),
	states: {
		error: {
			type: "final",
		},

		data: {
			initial: "idle",
			states: {
				idle: {},

				fetching: {
					always: [
						{
							guard: "pageExistsOnDisk",
							target: "fetchingFromDisk",
						},
						{
							target: "fetchingFromRemote",
						},
					],
				},

				fetchingFromDisk: {
					invoke: {
						src: "loadPageContentsFromDisk",
						input: ({ context }) => ({
							documentPath: context.documentPath!,
							pageId: context.pageId,
						}),
						onDone: {
							actions: assign(({ context, event }) => ({
								pageData: event.output.parsed,
								editorState: createEditorState({
									workspaceName: context.workspaceName,
									documentId: context.documentId,
									pageId: context.pageId,
									doc: event.output.parsed.doc,
								}),
								markdownEditorState: createMarkdownEditorState(event.output.raw, {
									theme: context.theme,
								}),
							})),
							target: ["editing", "#page.compilation.compiling"],
						},
					},
				},
				fetchingFromRemote: {
					invoke: {
						src: "loadPageContentsFromRemote",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							pageId: context.pageId,
						}),
						onDone: {
							target: ["editing", "#page.compilation.compiling"],
							actions: assign(({ context, event }) => ({
								pageData: event.output,
								editorState: createEditorState({
									workspaceName: context.workspaceName,
									documentId: context.documentId,
									pageId: context.pageId,
									doc: event.output.doc,
								}),
							})),
						},
						onError: {
							target: "#page.error",
							actions: assign(({ event }) => ({
								fetchError: new Error("Failed to fetch page data", {
									cause: event.error,
								}),
							})),
						},
					},
				},

				editing: {
					on: {
						"page.opened": {
							target: "editing",
							reenter: true,
							actions: enqueueActions(({ context, enqueue, event }) => {
								enqueue.assign(
									event.payload?.mode !== undefined
										? {
												editorMode: event.payload?.mode,
											}
										: {}
								);

								// If the theme has changed, we need to recreate the editor state with the new extensions
								if (event.payload?.theme && context.theme !== event.payload.theme) {
									enqueue.assign({
										theme: event.payload?.theme,
										markdownEditorState: createMarkdownEditorState(
											context.markdownEditorState?.doc?.toString() ?? "",
											{ theme: event.payload?.theme }
										),
									});
								}
							}),
						},
						"page.nameChanged": {
							actions: [
								assign(({ context, event }) => ({
									pageData: {
										...context.pageData!,
										metadata: {
											...context.pageData!.metadata,
											title: event.payload.newName,
										},
									},
								})),
								sendParent(({ context, event }) => ({
									type: "page.nameChanged",
									payload: {
										pageId: context.pageId,
										newName: event.payload.newName,
									},
								})),
							],
						},
						"page.transactionDispatched": {
							actions: "applyTransaction",
							target: "editing",
							reenter: true,
						},
					},
				},

				// ! "Closed" is not used right now
				// TODO: Use this to close the page when the user navigates away from it
				// closed: {
				// 	after: {
				// 		30_000: {
				// 			description: "Clear the editor state and compiled cells out of memory",
				// 			target: "idle",
				// 			actions: "clearEditorState",
				// 		},
				// 	},
				// },
			},
			on: {
				"page.opened": [
					{
						actions: assign(({ event }) =>
							event.payload?.mode !== undefined
								? {
										editorMode: event.payload?.mode,
									}
								: {}
						),
						target: "#page.data.fetching",
					},
				],
			},
		},

		collab: {
			initial: "disabled",
			on: {
				"document.collab.enabled": {
					actions: assign({
						collabEnabled: true,
					}),
				},
				"document.collab.disabled": {
					target: ".disabled",
					actions: assign(({ context }) => ({
						collabEnabled: false,
						editorState: context.editorState.reconfigure({
							plugins: context.editorState.plugins.filter(
								(plugin) => plugin.spec.key === collabKey
							),
						}),
					})),
				},
			},
			states: {
				disabled: {
					always: {
						guard: "isCollabEnabled",
						target: "connecting",
					},
				},
				connecting: {
					invoke: {
						src: "connectToReboot",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							pageId: context.pageId,
							authenticationActorRef: context.authenticationActorRef,
						}),
						onDone: {
							actions: assign({ reboot: ({ event }) => event.output }),
							target: "listening",
						},
					},
				},
				listening: {
					invoke: {
						src: "listenForChanges",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							pageId: context.pageId,
							editorState: context.editorState,
							reboot: context.reboot,
						}),
					},
					on: {
						"page.remoteTransactionReceived": {
							actions: "receiveRemoteTransaction",
						},
						"page.editorStateReconfigured": {
							actions: assign(({ event }) => ({
								editorState: event.payload.editorState,
							})),
						},
					},
				},
			},
		},

		queue: {
			initial: "idle",
			on: {
				"page.transactionDispatched": {
					guard: "isCollabEnabled",
					actions: assign(({ event }) => {
						if (!event.payload.transaction.docChanged) {
							return {};
						}
						return {
							hasPendingTransaction: true,
						};
					}),
				},
			},
			states: {
				error: {
					type: "final",
				},
				idle: {
					always: {
						guard: "hasPendingTransaction",
						target: "sendingToReboot",
						actions: assign({ hasPendingTransaction: false }),
					},
				},
				sendingToReboot: {
					invoke: {
						src: "sendToReboot",
						input: ({ context }) => ({
							reboot: context.reboot,
							editorState: context.editorState,
						}),
						onDone: {
							actions: assign(({ context, event }) => {
								if (!event.output) return;
								const transaction = receiveCommitTransaction(
									context.editorState,
									event.output
								);

								const editorState = context.editorState.apply(transaction);

								return {
									editorState,
									lastTransactionAppliedId: generateShortUuid(),
								};
							}),
							target: "idle",
						},
						onError: {
							target: "error",
						},
					},
				},
			},
		},

		persistence: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"page.transactionDispatched": {
							guard: and(["pageExistsOnDisk", not("isCollabEnabled")]),
							target: "debouncing",
						},
						"page.remoteTransactionReceived": {
							target: "debouncing",
						},
					},
				},
				debouncing: {
					after: {
						2_000: [
							{
								guard: and(["editorModeIsRich", not("isCollabEnabled")]),
								target: "persistingEditorState",
							},
							{ guard: "editorModeIsMarkdown", target: "persistingMarkdown" },
							{
								guard: and(["editorModeIsRich", "isCollabEnabled"]),
								target: "persistingConfirmedEditorState",
							},
						],
					},
				},
				persistingConfirmedEditorState: {
					invoke: {
						src: "writeConfirmedEditorState",
						input: ({ context }) => ({
							editorState: context.editorState,
							documentPath: context.documentPath,
							pageId: context.pageId,
						}),
						onDone: {
							target: "#page.persistence.idle",
						},
						onError: {
							actions: ({ event }) => {
								console.error(event);
							},
							target: "#page.persistence.idle",
						},
					},
				},
				persistingEditorState: {
					invoke: {
						src: "writeEditorStateToDisk",
						input: ({ context }) => ({
							pageId: context.pageId,
							documentPath: context.documentPath!,
							editorState: context.editorState!,
							transactionId: context.lastTransactionAppliedId!,
						}),
						onDone: {
							target: "#page.persistence.idle",
							actions: [
								assign(({ context, event }) => ({
									lastTransactionPersistedId: context.lastTransactionAppliedId,
									markdownEditorState: createMarkdownEditorState(
										event.output.raw,
										{ theme: context.theme }
									),
								})),
								sendParent(({ context }) => ({
									type: "page.changesPersisted",
									payload: {
										pageId: context.pageId,
										documentId: context.documentId,
									},
								})),
							],
						},
						onError: {
							target: "#page.persistence.idle",
						},
					},
					exit: sendParent({
						type: "page.persisted",
					}),
				},
				persistingMarkdown: {
					invoke: {
						src: "writeMarkdownEditorStateToDisk",
						input: ({ context }) => ({
							pageId: context.pageId,
							documentPath: context.documentPath!,
							editorState: context.markdownEditorState!,
							transactionId: context.lastTransactionAppliedId!,
						}),
						onDone: {
							target: "#page.persistence.idle",
							actions: [
								assign(({ context, event }) => ({
									lastTransactionPersistedId: context.lastTransactionAppliedId,
									editorState: createEditorState({
										workspaceName: context.workspaceName,
										documentId: context.documentId,
										pageId: context.pageId,
										doc: event.output.parsed,
									}),
								})),
								sendParent(({ context }) => ({
									type: "page.changesPersisted",
									payload: {
										pageId: context.pageId,
										documentId: context.documentId,
									},
								})),
							],
						},
						onError: {
							target: "#page.persistence.idle",
						},
					},
				},
			},
		},

		compilation: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"page.transactionDispatched": { target: "debouncing" },
						"page.compute": { target: "compiling" },
					},
				},
				debouncing: {
					after: { 2_000: { target: "compiling" } },
					on: {
						"page.compute": {
							target: "compiling",
						},
					},
				},
				compiling: {
					entry: assign(() => ({
						compilationError: null,
					})),
					invoke: {
						src: "compileCells",
						input: ({ context }) => ({
							pageId: context.pageId,
							previousCells: context.compiledCells,
							editorState: context.editorState!,
						}),
						onDone: {
							target: "#page.compilation.idle",
							actions: assign(({ event }) => ({
								compiledCells: event.output,
							})),
						},
						onError: {
							target: "#page.compilation.idle",
							actions: assign(({ event }) => ({
								compilationError: new Error("Compilation failed", {
									cause: event.error,
								}),
							})),
						},
					},
				},
			},
		},
	},

	on: {
		"document.cloned": {
			actions: assign(({ event }) => ({
				documentPath: event.payload.documentPath,
			})),
		},
		"page.archived": {
			actions: sendParent(({ context }) => ({
				type: "page.archived",
				payload: {
					pageId: context.pageId,
				},
			})),
		},
		"page.unarchived": {
			actions: sendParent(({ context }) => ({
				type: "page.unarchived",
				payload: {
					pageId: context.pageId,
				},
			})),
		},
		"page.refresh": {
			target: ".data.fetching",
		},
	},
});
